import React, { FC, HTMLAttributes, useEffect, useState } from "react";
import Headline from "./Headline";
import styled from "styled-components";
import ResolutionImg from "./image/ResolutionImg";
import { SeeMoreButton, SeeMoreCore } from "components/front/motion/SeeMore";
import { TabList } from "./TabView";
import OneStopManual, { OneStopTab } from "./flow/OneStopManual";
import TaxReturnManual, { TaxReturnTab } from "./flow/TaxReturnManual";
import { Attention, LinkText, ListItem, Note, Paragraph, UList } from "./flow/Literals";
import { imagePath } from "./image/util";
import { useDispatch, useSelector } from "react-redux";
import { animefurusatoSelector as Selector } from "modules/pages/lp/animefurusato/selector";
import { AnimefurusatoOperations as Operations } from "modules/pages/lp/animefurusato/operations";

export type Props = HTMLAttributes<HTMLDivElement>;
const DeductionFlowArea: FC<Props> = ({ ...props }) => {
  const dispatch = useDispatch();
  const showsDetail = useSelector(Selector.showsDeductionFlowDetail);
  const detailId = useSelector(Selector.deductionStepId);
  const onToggleShowsDetail = () => dispatch(Operations.toggleDeductionFlowDetail());
  useEffect(() => {
    if (window.location.hash === `#${detailId}`) {
      dispatch(Operations.openDeductionFlowDetail());
    }
  }, []);
  const [currentTab, setCurrentTab] = useState<"one-stop" | "tax-return">("one-stop");
  return (
    <Root {...props}>
      <StyledHeadline image={{ pc: "/flow/mascot-pc.png", sp: "/flow/mascot-sp.png", position: "left" }}>
        ＼次は！／
        <br />
        寄附から税金控除までの
        <br />
        流れを確認しよう！
      </StyledHeadline>
      <StepBox title="応援したい自治体に寄附する" stepIcon={{ pc: "/flow/step1-icon-pc.png", sp: "/flow/step1-icon-sp.png" }}>
        <StepBoxContent>
          <StepBoxContentImage pc={"/flow/step1-main-pc.png"} sp={"/flow/step1-main-sp.png"} loading="lazy" />
          <StepBoxContentText>
            <Paragraph>
              アニメファンドのサイトから好きなアニメとタイアップしている、または興味のある返礼品を取り扱っている自治体を選び、寄附の手続きを行います。
            </Paragraph>
            <Paragraph>自分の控除額を計算して適切な寄附額を申請しましょう。</Paragraph>
            <NarrowContent>
              <Note>複数の自治体に寄附することも可能です。</Note>
              <Note attention>
                <Attention>控除の限度額を超えた場合は負担額が加算されます。</Attention>
              </Note>
            </NarrowContent>
          </StepBoxContentText>
        </StepBoxContent>
      </StepBox>
      <StepArrow />
      <StepBox title="「返礼品」と「寄附金受領証明書」が届く" stepIcon={{ pc: "/flow/step2-icon-pc.png", sp: "/flow/step2-icon-sp.png" }}>
        <StepBoxContent>
          <StepBoxContentImage pc={"/flow/step2-main-pc.png"} sp={"/flow/step2-main-sp.png"} loading="lazy" />
          <StepBoxContentText>
            <Paragraph>発送の準備が完了次第、「返礼品」と「寄附金受領証明書」が配送されます。</Paragraph>
            <NarrowContent>
              <Note>
                「寄附金受領証明書」は<Attention>確定申告で控除をする場合</Attention>
                に必要になりますので大切に保管してください。寄附金の控除の方法についてはSTEP3をご覧ください。
              </Note>
            </NarrowContent>
          </StepBoxContentText>
        </StepBoxContent>
      </StepBox>
      <StepArrow />
      <StepBox title="寄附金控除の手続きを行う" stepIcon={{ pc: "/flow/step3-icon-pc.png", sp: "/flow/step3-icon-sp.png" }} id={detailId}>
        <StepBoxContent>
          <StepBoxContentImage pc={"/flow/step3-main-pc.png"} sp={"/flow/step3-main-sp.png"} loading="lazy" />
          <StepBoxContentText>
            <Paragraph>税金の控除を受けるためには２つの控除手続き方法から適切な方を選びます。</Paragraph>
            <NarrowContent>
              <StepHeadline>◆「ワンストップ特例制度」の申請をする</StepHeadline>
              <StyledUList>
                <ListItem>
                  年間の自治体への寄附が<span style={{ color: "#0700FF" }}>5箇所以内</span>の場合
                </ListItem>
                <ListItem>
                  他に確定申告が必要な事案が<span style={{ color: "#0700FF" }}>ない</span>
                </ListItem>
              </StyledUList>
              <Note>申請用紙が必要になります。</Note>
              <Note>申請の際には本人確認書類の写しが必要になります。</Note>
              <Note>
                申請用紙は自治体から返礼品とともにに配送されることもありますが、
                <LinkText href={imagePath("/寄附金税額控除に係る申告特例申請書.pdf")}>こちら</LinkText>からもDLできます。
              </Note>
              <Note>申請書類は期日までに自治体に郵送する必要があります。</Note>
              <Note>こちらの対象者は「確定申告」でも申請が可能です。</Note>
              <StepHeadline>◆「確定申告」をする</StepHeadline>
              <StyledUList>
                <ListItem>
                  年間の自治体への寄附が<span style={{ color: "#EB0000" }}>6箇所以上</span>の場合
                </ListItem>
                <ListItem>
                  他に確定申告が必要な事案が<span style={{ color: "#EB0000" }}>ある</span>
                </ListItem>
              </StyledUList>
              <Note>同じ自治体であれば複数寄附しても1箇所として計算します。</Note>
              <Note>税務署に提出する際には返礼品とともに届く「寄附金受領証明書」が必要です。</Note>
            </NarrowContent>
          </StepBoxContentText>
        </StepBoxContent>
        <SeeMoreCore
          shows={showsDetail}
          onToggleShows={onToggleShowsDetail}
          threshold={0}
          moreText="詳細を確認する"
          color="white"
          Button={StyledSeeMoreButton}
        >
          <DetailRoot>
            <TabList>
              <OneStopTab active={currentTab === "one-stop"} onClick={() => setCurrentTab("one-stop")} />
              <TaxReturnTab active={currentTab === "tax-return"} onClick={() => setCurrentTab("tax-return")} />
            </TabList>
            <OneStopManual active={currentTab === "one-stop"} />
            <TaxReturnManual active={currentTab === "tax-return"} />
          </DetailRoot>
        </SeeMoreCore>
      </StepBox>
    </Root>
  );
};
export default DeductionFlowArea;

const StepBox: FC<HTMLAttributes<HTMLDivElement> & { stepIcon: { pc: string; sp: string }; title: string }> = ({
  stepIcon: { pc, sp },
  title,
  children,
  ...props
}) => {
  return (
    <StepBoxRoot {...props}>
      <StepBoxHeader>
        <StepBoxHeaderImage pc={pc} sp={sp} loading="lazy" />
        <StepBoxHeaderTitle>{title}</StepBoxHeaderTitle>
      </StepBoxHeader>
      {children}
    </StepBoxRoot>
  );
};

const StyledSeeMoreButton = styled(SeeMoreButton)`
  && {
    margin: 20px 0;
  }
`;

const Root = styled.div``;
const StyledHeadline = styled(Headline)`
  margin: 20px 0;
`;
const StepBoxRoot = styled.div`
  background-color: #fff;
  border-radius: 10px;
  border: 2px solid #fff;
`;

const StepHeadline = styled.h6`
  margin-top: 26px;
  line-height: 1.6em;
`;
const StyledUList = styled(UList)`
  margin: 5px 0 16px;
`;
const StepArrow = styled.div`
  width: 0;
  height: 0;
  border-top: 20px solid #d4145b;
  @media screen and (max-width: 960px) {
    margin: 20px auto;
    border-right: 40px solid transparent;
    border-left: 40px solid transparent;
  }
  @media screen and (min-width: 961px) {
    margin: 30px auto;
    border-top: 20px solid #d4145b;
    border-right: 60px solid transparent;
    border-left: 60px solid transparent;
  }
`;
const StepBoxHeader = styled.div`
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  background-color: #101010;
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  @media screen and (min-width: 961px) {
    justify-content: center;
  }
`;
const StepBoxHeaderImage = styled(ResolutionImg)`
  width: 40px;
  height: 40px;
  @media screen and (min-width: 961px) {
    width: 54px;
    height: 54px;
  }
`;

const StepBoxHeaderTitle = styled.div`
  padding: 0 10px;
  @media screen and (min-width: 961px) {
    font-size: 1.2em;
  }
`;
const StepBoxContent = styled.div`
  text-align: center;
  @media screen and (max-width: 960px) {
    padding: 20px 10px;
  }
  @media screen and (min-width: 961px) {
    margin: 0 auto;
    padding: 30px;
    max-width: 620px;
  }
  @media screen and (min-width: 1441px) {
    max-width: 700px;
  }
`;
const StepBoxContentImage = styled(ResolutionImg)`
  height: 60px;
  @media screen and (min-width: 961px) {
    height: 80px;
  }
`;
const StepBoxContentText = styled.div`
  text-align: left;
  line-height: 1.428em;
  margin: 20px 0;
  color: #1a1a1a;
  @media screen and (min-width: 961px) {
    display: flex;
    align-items: center;
    flex-direction: column;
  }
`;
const DetailRoot = styled.div`
  color: #1a1a1a;
  @media screen and (max-width: 960px) {
    margin: 0 5px;
  }
  @media screen and (min-width: 961px) {
    margin: 0 30px;
  }
`;
const NarrowContent = styled.div`
  @media screen and (min-width: 961px) {
    max-width: 460px;
  }
  @media screen and (min-width: 1441px) {
    max-width: 500px;
  }
`;
